/**
 * API Client Configuration
 * 
 * Centralized Axios instance with authentication, error handling, and interceptors.
 * Includes tenant-aware URL transformation for multi-tenant support.
 */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_URL } from './config';

// =============================================================================
// Types
// =============================================================================

export interface ApiResponse<T> {
  data: T;
  meta?: {
    request_id: string;
    timestamp: string;
    [key: string]: unknown;
  };
}

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

/**
 * Plan resource usage snapshot returned by listings under `meta.plan_usage`
 * (REQ-BIL-017). All fields are null when the plan has no limit for the
 * resource.
 */
export interface PlanUsage {
  resource: string;
  used: number | null;
  limit: number | null;
  remaining: number | null;
  pct: number | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
    request_id: string;
    timestamp: string;
    plan_usage?: PlanUsage | null;
  };
}

// =============================================================================
// Configuration
// =============================================================================

const API_BASE_URL = API_URL;
const API_TIMEOUT = 30000;

// Routes that don't require tenant prefix
const NON_TENANT_ROUTES = [
  '/api/v1/auth/',
  '/api/v1/admin/',
  '/api/v1/agent-downloads/',
  '/api/v1/tenants/',       // Already contains tenant ID — avoid double prefixing
  '/api/v1/gateway-admin/', // AI Gateway control API: tenant derived server-side
  '/health',
  '/docs',
  '/openapi.json',
];

export function isNonTenantRoute(path: string): boolean {
  return NON_TENANT_ROUTES.some((route) => path.startsWith(route));
}

// =============================================================================
// Tenant-Aware API Client Class
// =============================================================================

class TenantAwareApiClient {
  private axiosInstance: AxiosInstance;
  private tenantId: string | null = null;

  constructor() {
    this.axiosInstance = axios.create({
      // NOTE: No baseURL — callers pass full paths (/api/v1/...) and buildUrl()
      // returns absolute paths. Setting baseURL would double the /api/ prefix.
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Send cookies for session management
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Add request ID for tracing
        config.headers['X-Request-ID'] = crypto.randomUUID();
        
        // Add timestamp
        config.headers['X-Request-Time'] = new Date().toISOString();
        if (this.tenantId) config.headers['X-Tenant-ID'] = this.tenantId;
        
        // Get access token from session storage (set by Ory)
        const session = sessionStorage.getItem('ory_session');
        if (session) {
          try {
            const { access_token } = JSON.parse(session);
            if (access_token) {
              config.headers.Authorization = `Bearer ${access_token}`;
            }
          } catch {
            // Invalid session, clear it
            sessionStorage.removeItem('ory_session');
          }
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
        
        // Handle 401 Unauthorized
        // NOTE: Do NOT redirect here - let AuthProvider handle auth redirects
        // Redirecting here causes loops when Kratos session is valid but API returns 401
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          // Just log and reject - AuthProvider will handle redirect if needed
          console.warn('[API Client] Received 401 - auth will be handled by AuthProvider');
          
          return Promise.reject(error);
        }
        
        // Handle 403 Forbidden
        if (error.response?.status === 403) {
          console.error('Access denied:', error.response.data?.error?.message);
        }
        
        // Handle 500+ Server errors
        if (error.response && error.response.status >= 500) {
          console.error('Server error:', error.response.data?.error?.message);
        }
        
        // Handle network errors
        if (!error.response) {
          console.error('Network error - server may be unavailable');
        }
        
        return Promise.reject(error);
      }
    );
  }

  /**
   * Set the current tenant ID for all subsequent requests.
   * Pass null to clear the tenant.
   */
  setTenant(tenantId: string | null): void {
    this.tenantId = tenantId;
  }

  /**
   * Get the current tenant ID.
   */
  getTenant(): string | null {
    return this.tenantId;
  }

  /**
   * Build the full URL with tenant prefix if required.
   */
  private buildUrl(path: string): string {
    // Check if this is a non-tenant route
    if (isNonTenantRoute(path)) {
      return path;
    }

    // For tenant-scoped routes, require tenant to be set
    if (!this.tenantId) {
      // During initial load (auth/me), we may not have tenant yet
      // Allow the request to proceed - the backend will handle missing tenant
      console.warn(`Tenant not set for request to: ${path}`);
      return path;
    }

    // Transform path: /api/v1/agents -> /api/v1/tenants/{tenant}/agents
    if (path.startsWith('/api/v1/')) {
      const restOfPath = path.replace('/api/v1/', '');
      return `/api/v1/tenants/${this.tenantId}/${restOfPath}`;
    }

    // For other paths, just prepend tenant
    return `/api/v1/tenants/${this.tenantId}${path}`;
  }

  /**
   * Make a GET request.
   */
  async get<T>(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get<T>(this.buildUrl(path), config);
  }

  /**
   * Make a POST request.
   */
  async post<T>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(this.buildUrl(path), data, config);
  }

  /**
   * Make a PUT request.
   */
  async put<T>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(this.buildUrl(path), data, config);
  }

  /**
   * Make a PATCH request.
   */
  async patch<T>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.patch<T>(this.buildUrl(path), data, config);
  }

  /**
   * Make a DELETE request.
   */
  async delete(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
    return this.axiosInstance.delete(this.buildUrl(path), config);
  }

  /**
   * Direct access to axios instance for special cases (file uploads, etc.)
   */
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

// =============================================================================
// Singleton Instance
// =============================================================================

export const apiClient = new TenantAwareApiClient();

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Extract error message from API error response
 */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiError>;
    // Standard API error format: { error: { message } }
    if (axiosError.response?.data?.error?.message) {
      return axiosError.response.data.error.message;
    }
    // FastAPI HTTPException format: { detail: { message } } or { detail: "string" }
    const detail = (axiosError.response?.data as unknown as Record<string, unknown>)?.detail;
    if (detail) {
      if (typeof detail === 'string') return detail;
      if (typeof detail === 'object' && detail !== null && 'message' in detail) {
        return (detail as { message: string }).message;
      }
    }
    if (axiosError.message) {
      return axiosError.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

/**
 * Extract error code from API error response
 */
export function getErrorCode(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiError>;
    if (axiosError.response?.data?.error?.code) {
      return axiosError.response.data.error.code;
    }
    // FastAPI HTTPException format: { detail: { code } }
    const detail = (axiosError.response?.data as unknown as Record<string, unknown>)?.detail;
    if (detail && typeof detail === 'object' && detail !== null && 'code' in detail) {
      return (detail as { code: string }).code;
    }
  }
  return null;
}

// =============================================================================
// Export
// =============================================================================

/** Raw Axios instance WITHOUT tenant-aware URL transformation. For system-level endpoints. */
export const rawApi: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/v1`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Set up request interceptor for rawApi (diagnostic headers only — auth is via cookie_session)
rawApi.interceptors.request.use(
  (config) => {
    config.headers['X-Request-ID'] = crypto.randomUUID();
    config.headers['X-Request-Time'] = new Date().toISOString();
    return config;
  },
  (error) => Promise.reject(error),
);

export default apiClient;
export { API_BASE_URL };
